import { useEffect, useState } from 'react'
import CreateMeetingModal from '@/pages/Home/components/CreateMeetingModal'
import { MeetingTitleTakenError, useCreateMeetingFlow } from '@/hooks/useCreateMeetingFlow'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { resolveDefaultMeetingName } from '@/utils/resolveDefaultMeetingName'
import { resolveTeamsMeetingName } from '@/utils/resolveTeamsMeetingName'
import { fetchCurrentCalendarEvent } from '@/services/current-event'

interface CreateMeetingModalHostProps {
  initialTranscriptionSource?: 'browser' | 'teams-native' | 'google-meet-native'
  initialNativeMeetingReference?: string
  /**
   * Calendar title of the meeting this modal was opened from (Teams side
   * panel). When set, the suggested name becomes
   * `<title>_<YYYYMMDD>_<run>` instead of `Meeting#N`.
   */
  meetingTitleSeed?: string
  /** Forwarded to `CreateMeetingModal`; `'panel'` is the Teams side-panel form. */
  variant?: 'default' | 'panel'
}

export default function CreateMeetingModalHost({
  initialTranscriptionSource,
  initialNativeMeetingReference,
  meetingTitleSeed,
  variant,
}: CreateMeetingModalHostProps = {}) {
  const { t } = useUiStrings()
  const show = useStore((s) => s.showCreateMeetingModal)
  const close = useStore((s) => s.closeCreateMeetingModal)
  const savedMeetingCount = useStore((s) => s.savedMeetingCount)
  const { startMeeting, error, setError } = useCreateMeetingFlow()
  const [defaultTitle, setDefaultTitle] = useState('')
  const [resolvingName, setResolvingName] = useState(false)
  const [suggestedProjectKey, setSuggestedProjectKey] = useState('')

  // When the modal opens, derive the default name from fresh server data so a
  // stale `savedMeetingCount` (e.g. when starting a meeting from the Detail
  // screen) cannot produce a duplicate `Meeting#N`.
  useEffect(() => {
    if (!show) return undefined

    let ignore = false
    setResolvingName(true)
    setDefaultTitle('')
    setSuggestedProjectKey('')

    // An ad-hoc Teams call carries no calendar title, and a tenant that never
    // granted the RSC permission reports none either — both fall through to
    // the usual `Meeting#N`.
    //
    // Outside the Teams panel the calendar is asked the same question the
    // panel is handed: the meeting the reader is sitting in names this one
    // (BRAI-745). It answers `{ event: null }` rather than failing, so the
    // fallback below is the normal path, not an error path.
    const resolving = meetingTitleSeed
      ? resolveTeamsMeetingName(meetingTitleSeed)
      : fetchCurrentCalendarEvent().then(({ event, projectKey }) => {
          if (!ignore && projectKey) setSuggestedProjectKey(projectKey)
          return event
            ? resolveTeamsMeetingName(event.title)
            : resolveDefaultMeetingName(savedMeetingCount)
        })

    resolving
      .then((name) => {
        if (!ignore) setDefaultTitle(name)
      })
      .finally(() => {
        if (!ignore) setResolvingName(false)
      })

    return () => {
      ignore = true
    }
  }, [show, savedMeetingCount, meetingTitleSeed])

  if (!show) return null

  const handleStart = async (payload: Parameters<typeof startMeeting>[0]) => {
    try {
      await startMeeting(payload)
      close()
    } catch (err) {
      // Let the modal handle the duplicate-title case inline next to the
      // Meeting Name input; the top toast is for unrelated failures only.
      if (err instanceof MeetingTitleTakenError) {
        throw err
      }
      setError(err instanceof Error ? err.message : t.tabAudioCaptureFailed)
    }
  }

  return (
    <>
      {error && (
        <div className="fixed inset-x-4 top-4 z-[60] mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-lg">
          <div className="flex items-start justify-between gap-3">
            <p>{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="shrink-0 text-red-500 hover:text-red-700"
              aria-label={t.close}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <CreateMeetingModal
        defaultTitle={defaultTitle}
        suggestedProjectKey={suggestedProjectKey}
        isResolvingTitle={resolvingName}
        initialTranscriptionSource={initialTranscriptionSource}
        initialNativeMeetingReference={initialNativeMeetingReference}
        variant={variant}
        onClose={close}
        onStart={handleStart}
      />
    </>
  )
}
